import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm } from 'react-hook-form';
import { useCustomizeParams } from '@/features/experiences/components/CustomizeMode/useCustomizeParams';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useExperienceHomeOrg } from '@/features/experiences/hooks/useExperienceHomeOrg';
import { useAddFeedToExperience } from '@/features/sources/hooks/useAddFeedToExperience';
import { useLibrary } from '@/pages/Dashboard/hooks/useLibrary';
import { LibraryItemType } from '@/types/gql.generated';
import {
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  Icon,
  Select,
  Text,
} from '@/ui';
import { getErrorMessage, isGQLErrorOfType } from '@/utils/errors';
import {
  agendaHeroSourceFormSchema,
  type AgendaHeroSourceFormValues,
} from './agendaHeroSourceForm.schema';

const FEED_TYPES = [
  LibraryItemType.Page,
  LibraryItemType.Printable,
  LibraryItemType.Planner,
];

type Props = {
  onBack: () => void;
};

export const AgendaHeroSource = ({ onBack }: Props) => {
  const { experience } = usePageExperienceContext();
  const { selectFeed } = useCustomizeParams();
  const { addFeedToExperience, isPending, error } = useAddFeedToExperience();
  // Sources follow the experience's home context: an org-homed experience
  // offers that org's library. Everything else — personally-homed, or the
  // editor isn't a member of the home org (direct share) — offers the
  // editor's personal library.
  const { sharedOrgId } = useExperienceHomeOrg(experience);
  const { data: experiences = [], isPending: isLoadingExperiences } =
    useLibrary(undefined, {
      filters: { types: FEED_TYPES },
      ...(sharedOrgId && { organizationId: sharedOrgId }),
    });
  const toast = useToast();

  const {
    handleSubmit: onSubmit,
    register,
    watch,
  } = useForm<AgendaHeroSourceFormValues>({
    defaultValues: { feedId: '' },
    resolver: standardSchemaResolver(agendaHeroSourceFormSchema),
  });

  // Exclude the current experience — the server rejects adding an experience as
  // a feed of itself.
  const options = experiences.filter((item) => item.id !== experience.id);
  const selectedFeedId = watch('feedId');

  // Most experiences aren't discoverable, so the server rejects them with a
  // permission error; show a clearer message than the raw GraphQL text.
  const errorMessage = error
    ? isGQLErrorOfType(error, 'InvalidPermission')
      ? "This experience can't be added as a source."
      : getErrorMessage(error)
    : null;

  const handleSubmit = ({ feedId }: AgendaHeroSourceFormValues) => {
    addFeedToExperience(feedId, {
      onSuccess: (data) => {
        toast.notify('✨ Source added!');
        selectFeed(data.addFeedToExperience.id);
      },
    });
  };

  return (
    <Box maxW="540px">
      <Flex align="center" gap={3} mb={6}>
        <Button
          color="ink.soft"
          flexShrink={0}
          h="auto"
          minW={0}
          p={0}
          variant="link"
          onClick={onBack}
        >
          <Icon icon="ArrowLeft" size={4} />
        </Button>
        <Text
          color="ink.strong"
          fontSize="26px"
          fontWeight={700}
          letterSpacing="-0.02em"
        >
          Add from Agenda Hero
        </Text>
      </Flex>

      <form noValidate onSubmit={onSubmit(handleSubmit)}>
        <Text color="ink.soft" fontSize="ah-control" mb={4}>
          Choose an experience to pull events from:
        </Text>

        {errorMessage && (
          <Alert mb={5} status="error">
            <AlertIcon />
            <AlertTitle>{errorMessage}</AlertTitle>
          </Alert>
        )}

        <FormControl mb={5}>
          <Select
            aria-label="Choose an experience to pull events from"
            isDisabled={isLoadingExperiences}
            placeholder="Select an experience"
            {...register('feedId')}
          >
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title ?? 'Untitled experience'}
              </option>
            ))}
          </Select>
          <FormHelperText>
            Add another Agenda Hero experience as a source.
          </FormHelperText>
        </FormControl>

        <Flex justify="flex-end">
          <Button
            isDisabled={!selectedFeedId}
            isLoading={isPending}
            type="submit"
          >
            Add source
          </Button>
        </Flex>
      </form>
    </Box>
  );
};
